/* eslint-disable react/prop-types */
import NextApp from 'next/app';
import getConfig from 'next/config';
import React, { useEffect } from 'react';
import Head from 'next/head';
import Router from 'next/router';
import useSWR from 'swr';
import { ThemeProvider, StyleSheetManager } from 'styled-components';
// eslint-disable-next-line import/no-extraneous-dependencies
import isPropValid from '@emotion/is-prop-valid';
import { LazyMotion } from 'framer-motion';

import { theme, GlobalStyle } from '@glowforge/gf-styled/src/theme';
import PageMetadata from '@glowforge/gf-styled/src/components/contentful-views/page-metadata';
import { interpretMetadata } from '@glowforge/gf-styled/src/models/page-metadata';

import AppGlobalStyle from './app.styles';
import Scripts from '../components/scripts';
import Analytics from '../components/analytics';
import Footer from '../components/footer';
import RouterProgressBar from '../components/router-progress-bar/router-progress-bar';
import UserProvider from '../context/UserContext/UserProvider';
import LocationProvider from '../context/LocationContext/LocationProvider';
import ReferralProvider from '../context/ReferralContext/ReferralProvider';
import ModalPriorityProvider from '../context/ModalPriorityContext/ModalPriorityProvider';
import { getDefaultMetadata, getGlobalFooterRebrand } from '../api/contentful';
import { getNavbar } from '../api/navbar';
import { REACT_ROOT } from '../constants';
import { trackCoreWebVitals } from '../util/web-vitals';
import { captureMarketingAttributionFromUrl } from '../util/marketing-attribution';
import { observeUtmPage } from '../util/utm-observability';
import Navbar from '../components/navbar';

const loadFeatures = () =>
  import('@glowforge/gf-styled/src/util/framer-load-dom-features').then(
    (res) => res.default,
  );

const {
  publicRuntimeConfig: { GF_DOTCOM_GF_API_ENDPOINT },
} = getConfig();

const APP_CACHE_KEY = 'app';

/**
 * Fetch data required by all pages
 */
const fetchData = async () => {
  const [defaultMetadata, footerRebrandContent] = await Promise.all([
    getDefaultMetadata(),
    getGlobalFooterRebrand(),
  ]);

  return {
    defaultMetadata,
    footerRebrandContent,
  };
};

const App = ({
  initialData,
  countryCode,
  stateCode,
  pageProps,
  Component,
  builderNav
}) => {

  const { data: appData } = useSWR(APP_CACHE_KEY, () => fetchData(), {
    fallbackData: initialData,
    revalidateOnMount: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const { defaultMetadata, footerRebrandContent } = appData;

  const {
    includeFooter = true,
    includeNav = true,
    hideBuyButton = false,
  } = pageProps;

  const interpretedMetadata = interpretMetadata(defaultMetadata);
  const { pageTitle: defaultTitle, ...trimmedMetadata } = interpretedMetadata;

  useEffect(() => {
    trackCoreWebVitals();
    captureMarketingAttributionFromUrl();
    observeUtmPage({ initialPage: true });

    const handleRouteChangeComplete = (url) => {
      captureMarketingAttributionFromUrl(url);
      observeUtmPage({ url });
    };
    Router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      Router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, []);

  /* eslint-disable react/jsx-props-no-spreading, jsx-a11y/html-has-lang */
  return (
    <StyleSheetManager
      enableVendorPrefixes
      // eslint-disable-next-line react/jsx-no-bind
      shouldForwardProp={(propName, elementToBeRendered) =>
        typeof elementToBeRendered === 'string' ? isPropValid(propName) : true
      }>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <AppGlobalStyle />
        <ModalPriorityProvider>
          <LocationProvider>
            <Scripts />
            <UserProvider>
              <ReferralProvider>
                <div className='app'>
                  <Analytics />
                  <Head>
                    <link
                      rel='alternate'
                      type='application/rss+xml'
                      title='Glowforge - the 3D Laser Printer » Home Comments Feed'
                      href='https://blog.glowforge.com/feed/'
                    />
                    <link rel='shortcut icon' href='/dotcom/favicon.png' />
                    <meta httpEquiv='x-ua-compatible' content='ie=edge' />
                    <meta name='robots' content='noodp' />
                    <meta property='og:locale' content='en_US' />
                    <meta name='viewport' content='width=device-width' />
                    <meta charSet='utf-8' />
                    <script
                      type='text/javascript'
                      /* eslint-disable max-len */
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: `
                        window._gf_react_root = '${REACT_ROOT}';
                        window.GF_MADLIB_ENDPOINT='${GF_DOTCOM_GF_API_ENDPOINT}/functions/put-email';
                        window.GF_COUNTRY_CODE='${countryCode}';
                        window.GF_STATE_CODE='${stateCode}';
                      `,
                      }}
                    /* eslint-enable max-len */
                    />
                  </Head>
                  <PageMetadata
                    {...trimmedMetadata}
                    pageTitle={defaultTitle || 'Glowforge'}
                  />
                  {/* lazy loading framer motion animations. */}
                  <LazyMotion features={loadFeatures} strict>
                    {includeNav ? (
                      <Navbar {...{ ...builderNav, hideBuyButton }} />
                    ) : null}
                    <RouterProgressBar includeNav={includeNav} />
                    <Component {...pageProps} />
                    <Footer
                      includeFooter={includeFooter}
                      footerRebrandContent={footerRebrandContent}
                    />
                  </LazyMotion>
                </div>
              </ReferralProvider>
            </UserProvider>
          </LocationProvider>
        </ModalPriorityProvider>
      </ThemeProvider>
    </StyleSheetManager>
  );
};

App.getInitialProps = async (appContext) => {
  const [initialData, appProps] = await Promise.all([
    fetchData(),
    NextApp.getInitialProps(appContext),
  ]);

  // Used to get initial builderNav data -> update happens in Navbar component
  const builderNav = await getNavbar();
  return {
    initialData,
    builderNav,
    ...appProps,
  };
};

export default App;
